import { QuotedSidePanel } from 'components/secondaryMarket/live/sidePanel/quoted/QuotedSidePanel';
import { connect } from 'react-redux';
import type { FormOwnProps } from 'shared/connect/connect';
import { logAnalytics } from 'shared/middlewares/analytics/logger';
import { liveInputFormActions } from 'state/secondaryMarket/live/form/input/inputForm.actions';
import { liveFormInputSelectors } from 'state/secondaryMarket/live/form/input/inputForm.selectors';
import { liveSelectors } from 'state/secondaryMarket/live/live.selector';
import {
  getLiveInitialUpfront,
  getLiveQuoteFormattedAccruedInterest,
  getLiveQuotePriceType,
  getLiveQuoteSalesAdjustedCleanPriceFormatted,
  getLiveQuoteSalesMarginFormatted,
  getLiveQuoteSettlementDate,
  getLiveQuoteTradeAuthDurationInMillis,
  getLiveQuoteTradeAuthStartTimeInMillis,
  getLiveQuoteTradeWay,
  isLiveQuoteEnded,
} from 'state/secondaryMarket/live/quote/liveQuote.selector';
import {
  secondaryMarketLiveQuoteCancelRequestedThunk,
  secondaryMarketLiveRepriceQuoteThunk,
  secondaryMarketLiveTradeRequestedThunk,
} from 'state/secondaryMarket/live/quote/liveQuote.thunk';
import { liveReferentialSelectors } from 'state/secondaryMarket/live/referential/liveReferential.selector';
import type {
  SecondaryPriceType,
  SecondaryQuotationType,
} from 'state/secondaryMarket/productSearch/productSearch.model';
import type { Trend } from 'state/secondaryMarket/realtime/realtime.model';
import type { StreamingPriceForDisplay } from 'state/secondaryMarket/realtime/realtime.selector';
import type { TradeWay } from 'state/secondaryMarket/secondaryMarket.model';
import { getUserFrontRights } from 'state/selectors';
import type { ThunkDispatch } from 'state/state.common';
import type { AppState } from 'state/state.model';

type QuotedSidePanelStateProps = {
  quotationType?: SecondaryQuotationType;
  canAdjustPrice: boolean;
  price: number;
  adjustedPrice: number;
  priceType: SecondaryPriceType;
  priceCurrency: string;
  canTrade: boolean;
  eligibleTraceReporting: boolean;
  traceReporting: boolean;
  salesMargin: number;
  settlementDate?: string;
  tradeWay: TradeWay;
  tradeAuthStartTimeInMillis: number;
  tradeAuthDurationInMillis: number;
  streamingPrice?: StreamingPriceForDisplay;
  accruedInterest?: number;
  cleanPrice?: number;
  isPriceAdjusted: boolean;
  isEnded: boolean;
  isLive: boolean;
  settlementPrice?: number;
  trend?: Trend;
  initialUpfront?: number;
};

type QuotedSidePanelDispatchProps = {
  onDone: (closeTab?: boolean, isCancel?: boolean) => void;
  onTraceReportingChanged: (value: boolean) => void;
  onTrade?: () => void;
  onReprice?: () => void;
};

type QuotedSidePanelOwnProps = FormOwnProps;

export type QuotedSidePanelProps = QuotedSidePanelDispatchProps & QuotedSidePanelOwnProps & QuotedSidePanelStateProps;

const mapStateToProps = (state: AppState, { formId }: QuotedSidePanelOwnProps): QuotedSidePanelStateProps => {
  const { quoteStatus: isLive = false, settlementPrice } =
    liveSelectors.getStreamingPriceForDisplay(state, formId) ?? {};
  return {
    accruedInterest: getLiveQuoteFormattedAccruedInterest(state, formId),
    adjustedPrice: liveSelectors.getSalesAdjustedSettlementPriceFormatted(state, formId)!,
    price: liveSelectors.getEffectivePriceFormatted(state, formId)!,
    priceType: (getLiveQuotePriceType(state, formId) ?? liveReferentialSelectors.getPriceType(state, formId))!,
    priceCurrency: liveReferentialSelectors.getCurrency(state, formId)!,
    settlementDate: getLiveQuoteSettlementDate(state, formId),
    tradeWay: getLiveQuoteTradeWay(state, formId)!,
    canAdjustPrice: liveSelectors.canAdjustPrice(state, formId),
    eligibleTraceReporting: liveReferentialSelectors.getEligibleTraceReporting(state, formId),
    traceReporting: liveFormInputSelectors.getFormInputTraceReporting(state, formId),
    canTrade: getUserFrontRights(state).secondaryTradeEnabled,
    isEnded: isLiveQuoteEnded(state, formId),
    salesMargin: getLiveQuoteSalesMarginFormatted(state, formId)!,
    tradeAuthDurationInMillis: getLiveQuoteTradeAuthDurationInMillis(state, formId)!,
    tradeAuthStartTimeInMillis: getLiveQuoteTradeAuthStartTimeInMillis(state, formId)!,
    cleanPrice: getLiveQuoteSalesAdjustedCleanPriceFormatted(state, formId),
    quotationType: liveSelectors.getQuotationType(state, formId!),
    streamingPrice: liveSelectors.getStreamingPriceForDisplay(state, formId),
    isPriceAdjusted: liveFormInputSelectors.isPriceAdjusted(state, formId),
    isLive,
    settlementPrice: settlementPrice?.value,
    trend: settlementPrice?.trend,
    initialUpfront: getLiveInitialUpfront(state, formId)!,
  };
};

const mapDispatchToProps = (
  dispatch: ThunkDispatch,
  { formId: tabId }: QuotedSidePanelOwnProps,
): QuotedSidePanelDispatchProps => {
  return {
    onDone: (closeTab, isCancel) => {
      if (isCancel) {
        logAnalytics.trackEvent('Secondary market', 'Cancel quote');
      }
      dispatch(secondaryMarketLiveQuoteCancelRequestedThunk(tabId, closeTab, isCancel));
    },
    onTraceReportingChanged(value) {
      dispatch(liveInputFormActions.formFieldChanged(tabId, 'traceReporting', value));
    },
    onTrade() {
      logAnalytics.trackEvent('Secondary market', 'Confirm trade');
      dispatch(secondaryMarketLiveTradeRequestedThunk(tabId));
    },
    onReprice: () => {
      dispatch(secondaryMarketLiveRepriceQuoteThunk(tabId));
    },
  };
};

export const connectQuotedSidePanel = connect(mapStateToProps, mapDispatchToProps);

export const ConnectedQuotedSidePanel = connectQuotedSidePanel(QuotedSidePanel);
